<template>
  <component
    :is="draggableComponent"
    :id="`draggable-level-${level}`"
    class="text-left drag-area"
    :class="{ 'pb-2': level > 1 }"
    tag="ul"
    @add="onAdd"
    @update="onUpdate"
    :list="items"
    :group="{ name: 'elements' }"
    :scrollSensitivity="200"
    :move="onMove"
    v-on="$listeners"
  >
    <li
      v-for="(element, idx) in items"
      :key="`${parentKey}-${idx}`"
      :id="idx"
      :class="element[childItemsKey] ? classesGroup : classesElement"
    >
      <slot
        name="listElement"
        v-bind="{
          ...element,
          idx,
          level,
          parentKey,
          elementKey: `${parentKey}-${idx}`,
          dotsItems: element.dotsItems || (element[childItemsKey] ? groupsDotsItems : elementsDotsItems) || [],
          disabled,
        }"
      >
        <draggable-list-item
          v-bind="{
            ...element,
            level,
            parentKey,
            hideableGroups,
            elementKey: `${parentKey}-${idx}`,
            dotsItems: element.dotsItems || (element[childItemsKey] ? groupsDotsItems : elementsDotsItems) || [],
            disabled,
            disableBorders,
            titleKey: itemTitleKey,
            childItemsKey: childItemsKey,
          }"
          v-on="$listeners"
          @toggleHideGroup="toggleHideGroup({ ...element, idx })"
        >
          <template v-for="(value, key) in $scopedSlots" #[key]="scope">
            <slot :name="key" v-bind="scope" />
          </template>
        </draggable-list-item>
      </slot>
      <draggable-list-draggable
        v-if="element[childItemsKey]"
        v-show="hideableGroups ? !element.isHidden : true"
        :items="element[childItemsKey]"
        :allow-elements-on-main-level="allowElementsOnMainLevel"
        :allow-one-element-on-main-level="allowOneElementOnMainLevel"
        :allow-nested-groups="allowNestedGroups"
        :disabled="disabled"
        :hideable-groups="hideableGroups"
        :disable-draggable="disableDraggable"
        :disable-borders="disableBorders"
        :elements-dots-items="elementsDotsItems"
        :groups-dots-items="groupsDotsItems"
        :item-title-key="itemTitleKey"
        :level="level + 1"
        :parent-key="`${parentKey}-${idx}`"
        :child-items-key="childItemsKey"
        v-on="$listeners"
      >
        <template v-for="(value, key) in $scopedSlots" #[key]="scope">
          <slot :name="key" v-bind="scope" />
        </template>
      </draggable-list-draggable>
    </li>
  </component>
</template>
<script>
import draggable from 'vuedraggable-multi';
import DraggableListItem from '@/components/DraggableListItem';
export default {
  name: 'DraggableListDraggable',
  components: { DraggableListItem },
  props: {
    allowElementsOnMainLevel: {
      type: Boolean,
      default: false,
    },
    allowOneElementOnMainLevel: {
      type: Boolean,
      default: false,
    },
    hideableGroups: {
      type: Boolean,
      default: false,
    },
    allowNestedGroups: {
      type: Boolean,
      default: false,
    },
    childItemsKey: {
      type: String,
      default: 'items',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    items: {
      type: Array,
      default: () => {
        return [];
      },
    },
    elementsDotsItems: {
      type: Array,
    },
    groupsDotsItems: {
      type: Array,
    },
    itemTitleKey: {
      type: String,
      default: 'name',
    },
    level: {
      type: Number,
      default: 1,
    },
    disableDraggable: {
      type: Boolean,
      default: false,
    },
    disableBorders: {
      type: Boolean,
      default: false,
    },
    parentKey: {
      type: [String, Number],
      default: '0',
    },
  },
  computed: {
    classesGroup() {
      return [...(this.disableBorders ? [...(this.level > 1 ? ['pl-6'] : [])] : ['li--element', 'px-6'])];
    },
    classesElement() {
      return this.level === 1 ? [...(this.disableBorders ? [] : ['li--element'])] : ['margin--bottom', 'border'];
    },
    draggableComponent() {
      return this.disableDraggable ? 'ul' : draggable;
    },
  },
  methods: {
    onAdd(e) {
      this.$emit('elementAdded', e);
    },
    onUpdate(e) {
      this.$emit('elementUpdated', e);
    },
    onMove(e) {
      if (!this.allowNestedGroups && e.draggedContext.element[this.childItemsKey] && e.to.id !== 'draggable-level-1') {
        return false;
      }
      if (
        !this.allowElementsOnMainLevel &&
        !e.draggedContext.element[this.childItemsKey] &&
        e.to.id === 'draggable-level-1'
      ) {
        return false;
      }
      if (this.allowOneElementOnMainLevel && e.to.id === 'draggable-level-1') return false;
    },
    toggleHideGroup(element) {
      if (element.groupName && element.id) {
        const hiddenGroups = JSON.parse(localStorage.getItem(`${element.groupName}-hidden-groups`) || '{}');
        localStorage.setItem(
          `${element.groupName}-hidden-groups`,
          JSON.stringify({
            ...hiddenGroups,
            [element.id]: !element.isHidden,
          })
        );
      }
      this.$set(this.items[element.idx], 'isHidden', !element.isHidden);
      this.$forceUpdate();
    },
  },
};
</script>

<style scoped lang="scss">
ul {
  list-style: none;
  padding-left: 0;
}
.margin--bottom {
  &:not(:last-child) {
    margin-bottom: 4px;
  }
}
.li--element {
  @include border(top);
  @include border(right);
  @include border(left);
  &:last-child {
    @include border(bottom);
  }
}
</style>
